import { useState, useEffect } from "react"
import { Button, Dropdown, Flex, Tooltip } from "antd"
import { CheckOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"
import { AddAvailabilityScheduleModal } from "../AddAvailabilityScheduleModal/AddAvailabilityScheduleModal"
import { AddOverrideModal } from "../AddOverrideModal/AddOverrideModal"
import { isServerId } from "../../../mappers/toApiPayload"
import { useWizardContext } from "../../../WizardContext"
import styles from "./AvailabilitySchedule.module.css"

const ALL_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
const DAY_LABELS = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
}

const TIMES_PREVIEW_COUNT = 9

const formatTimeRange = (from, to) => {
  if (!from && !to) return ""
  const fmt = (t) => {
    if (!t) return ""
    const [h, m] = t.split(":")
    const hour = Number.parseInt(h, 10)
    const ampm = hour >= 12 ? "PM" : "AM"
    const display = hour % 12 || 12
    return `${display}:${m} ${ampm}`
  }
  return `${fmt(from)}–${fmt(to)}`
}

const formatDayRange = (days) => {
  if (!days?.length) return ""
  return days.map((d) => DAY_LABELS[d] ?? d).join(", ")
}

const ScheduleItem = ({ item, onEdit, onDelete, isOverride }) => {
  const [expanded, setExpanded] = useState(false)
  const times = item.start_times || []
  const visibleTimes = expanded ? times : times.slice(0, TIMES_PREVIEW_COUNT)
  const hasMore = times.length > TIMES_PREVIEW_COUNT

  const moreItems = [
    { key: "delete", label: "Delete", danger: true, onClick: () => onDelete(item.id) },
  ]

  const innerContent = (
    <>
      <div className={styles.scheduleItemBody}>
        <div>
          <Text fw={500} className="block">
            {item.name || formatDayRange(item.days)}
          </Text>
          <Text type="secondary" size="sm">
            {[formatDayRange(item.days), formatTimeRange(item.from_time, item.to_time)]
              .filter(Boolean)
              .join(" • ")}
          </Text>
        </div>
        <Flex gap={4} wrap="wrap">
          {visibleTimes.map((t) => (
            <span key={t} className={styles.timeTag}>{t}</span>
          ))}
        </Flex>
        {hasMore && (
          <button type="button" className={styles.showAllBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? "Collapse" : `Show all ${times.length} times`}
          </button>
        )}
      </div>
      <div className={styles.scheduleItemActions}>
        <Tooltip title="Edit">
          <button type="button" className={styles.actionBtn} onClick={() => onEdit(item)}>
            <EditOutlined style={{ fontSize: 16 }} />
          </button>
        </Tooltip>
        <Dropdown menu={{ items: moreItems }} trigger={["click"]} placement="bottomRight">
          <button type="button" className={`${styles.actionBtn} ${styles.actionBtnLg}`}>
            <MoreOutlined style={{ fontSize: 18 }} />
          </button>
        </Dropdown>
      </div>
    </>
  )

  if (isOverride) {
    return (
      <div className={styles.overrideItem}>
        <div className={styles.overrideAccent} />
        <div className={styles.overrideItemBody}>{innerContent}</div>
      </div>
    )
  }

  return (
    <div className={styles.scheduleItem}>
      <div className={styles.scheduleItemContent}>{innerContent}</div>
    </div>
  )
}

ScheduleItem.propTypes = {
  item: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isOverride: PropTypes.bool,
}

export const AvailabilitySchedule = ({ form }) => {
  const { t } = useTranslation()

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState(null)
  const [editingOverride, setEditingOverride] = useState(null)

  const [schedules, setSchedules] = useState(() => form.getFieldValue("availability_schedules") || [])
  const [overrides, setOverrides] = useState(() => form.getFieldValue("schedule_overrides") || [])

  // availability_schedules / schedule_overrides have no Form.Item, so a mount-time
  // read can miss data that WizardProvider loads slightly later (and Form.useWatch
  // doesn't reliably track unregistered fields). Re-read from the form once it's
  // initialized — the reliable signal from the wizard context.
  const { isFormInitialized } = useWizardContext()
  useEffect(() => {
    if (!isFormInitialized) return
    setSchedules(form.getFieldValue("availability_schedules") || [])
    setOverrides(form.getFieldValue("schedule_overrides") || [])
  }, [isFormInitialized, form])

  // Hide _destroy schedules (pending backend deletion) and multi-day remnants.
  // After a multi→single switch a multi-day record lingers in availability_schedules
  // but isn't a single-day rule, so it must not render here (the save handler
  // destroys it server-side). Prefer the backend's schedule_type; fall back to shape
  // (start time only — no to_time / interval) for older records without the field.
  const isMultiDay = (schedule) =>
    schedule.schedule_type === "multi_day" ||
    (schedule.schedule_type == null && schedule.to_time == null && schedule.interval == null)
  const visibleSchedules = schedules.filter(
    (schedule) => !schedule._destroy && !isMultiDay(schedule)
  )
  const coveredDays = new Set(visibleSchedules.flatMap((schedule) => schedule.days || []))

  const handleSaveSchedule = (item) => {
    setSchedules((prev) => {
      const idx = prev.findIndex((schedule) => schedule.id === item.id)
      const next = idx >= 0 ? prev.map((schedule, i) => (i === idx ? item : schedule)) : [...prev, item]
      form.setFieldValue("availability_schedules", next)
      return next
    })
  }

  const handleDeleteSchedule = (id) => {
    setSchedules((prev) => {
      const target = prev.find((schedule) => schedule.id === id)
      // Persisted schedules must be sent back with _destroy so the backend removes
      // them; never-saved ones (client temp id) can just be dropped locally.
      const next = target && isServerId(target.id)
        ? prev.map((schedule) => (schedule.id === id ? { ...schedule, _destroy: true } : schedule))
        : prev.filter((schedule) => schedule.id !== id)
      form.setFieldValue("availability_schedules", next)
      return next
    })
  }

  const handleEditSchedule = (item) => {
    setEditingSchedule(item)
    setIsScheduleModalOpen(true)
  }

  const handleSaveOverride = (item) => {
    setOverrides((prev) => {
      const idx = prev.findIndex((o) => o.id === item.id)
      const next = idx >= 0 ? prev.map((o, i) => (i === idx ? item : o)) : [...prev, item]
      form.setFieldValue("schedule_overrides", next)
      return next
    })
  }

  const handleDeleteOverride = (id) => {
    setOverrides((prev) => {
      const next = prev.filter((o) => o.id !== id)
      form.setFieldValue("schedule_overrides", next)
      return next
    })
  }

  const handleEditOverride = (item) => {
    setEditingOverride(item)
    setIsOverrideModalOpen(true)
  }

  return (
    <>
      <Card
        className="mb-3xl"
        title={
          <Flex align="center" gap={6}>
            {t("listings.tabs.scheduling.sections.availability_schedule.title")}
            <InfoTooltip title={t("experiences.scheduling.sections.days_and_hours.tooltip")} />
          </Flex>
        }
      >
        {/* Schedule Summary box */}
        <div className={`${styles.summaryBox} mb-l`}>
          <Text fw={700} size="sm" className="block mb-s">
            {t("listings.tabs.scheduling.sections.availability_schedule.schedule_summary")}
          </Text>
          <div className={styles.summaryRow}>
            <Flex gap={4} align="center" wrap="nowrap">
              {ALL_DAYS.map((day) => {
                const isActive = coveredDays.has(day)
                return (
                  <div key={day} className={styles.summaryDay}>
                    <div className={`${styles.summaryDayInner} ${isActive ? styles.summaryDayInnerActive : ""}`}>
                      <Text
                        size="sm"
                        fw={500}
                        className={isActive ? "" : styles.summaryDayTextInactive}
                      >
                        {DAY_LABELS[day]}
                      </Text>
                    </div>
                    {isActive && (
                      <span className={styles.summaryCheckBadge}>
                        <CheckOutlined style={{ fontSize: 10, color: "#52c41a" }} />
                      </span>
                    )}
                  </div>
                )
              })}
            </Flex>
            <Button
              type="primary"
              shape="round"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingSchedule(null)
                setIsScheduleModalOpen(true)
              }}
            >
              {t("listings.tabs.scheduling.sections.availability_schedule.add_schedule")}
            </Button>
          </div>
        </div>

        {/* Schedule items */}
        {visibleSchedules.length === 0 ? (
          <Text type="secondary" className={styles.emptyState}>
            {t("listings.tabs.scheduling.sections.availability_schedule.empty_state")}
          </Text>
        ) : (
          <div className={styles.scheduleList}>
            {visibleSchedules.map((item) => (
              <ScheduleItem
                key={item.id}
                item={item}
                onEdit={handleEditSchedule}
                onDelete={handleDeleteSchedule}
              />
            ))}
          </div>
        )}

        {/* Override section */}
        {/* <div className={`${styles.overridesBar} mt-l`}>
          <Flex align="center" gap={8}>
            <Text fw={700} size="sm">
              {t("listings.tabs.scheduling.sections.availability_schedule.overrides_title")}
            </Text>
            {overrides.length > 0 && (
              <span className={styles.overrideBadge}>{overrides.length}</span>
            )}
          </Flex>
          <Button
            type="link"
            icon={<PlusOutlined />}
            className="p-0"
            onClick={() => {
              setEditingOverride(null)
              setIsOverrideModalOpen(true)
            }}
          >
            {t("listings.tabs.scheduling.sections.availability_schedule.add_override")}
          </Button>
        </div> */}

        {overrides.length > 0 && (
          <div className={`${styles.overrideList} mt-s`}>
            {overrides.map((item) => (
              <ScheduleItem
                key={item.id}
                item={item}
                onEdit={handleEditOverride}
                onDelete={handleDeleteOverride}
                isOverride
              />
            ))}
          </div>
        )}
      </Card>

      <AddAvailabilityScheduleModal
        open={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSave={handleSaveSchedule}
        existingSchedules={visibleSchedules}
        editingSchedule={editingSchedule}
      />

      <AddOverrideModal
        open={isOverrideModalOpen}
        onClose={() => setIsOverrideModalOpen(false)}
        onSave={handleSaveOverride}
        editingOverride={editingOverride}
      />
    </>
  )
}

AvailabilitySchedule.propTypes = {
  form: PropTypes.object.isRequired,
}
